/**
 * What the launcher searches and how it reads what you typed (RFC 0333).
 *
 * Pure, and apart from the component, because three places need it: the
 * launcher that draws the rows, the shell that builds the list, and
 * `startSession`, which names a session after its first message. These lived
 * in `CommandBar.tsx` while ⌘K was its own component; moving them here is what
 * let that component go.
 */

import { catalogRows, type ProjectDoc } from '@mod/core/model'

export interface CommandTarget {
  id: string
  title: string
  /** Shown after the title: a path, a branch, whatever disambiguates. */
  hint?: string
  /** `create` mints a record of a document type. It is an ACTION rather than a
   *  place, which is why it sorts above matches: "review" almost always means
   *  the reviews you have, but "new review" means only one thing. */
  /** `terminal` is a shell running on THIS machine (RFC 0276 §3). Its own kind
   *  rather than a document, because it is in no catalog and picking one
   *  re-attaches to a process rather than opening a record. */
  /** `web` is a page to open: a typed URL, one from history, or a web search,
   *  offered as a ROW beside the documents that also matched what you typed. */
  kind:
    | 'document'
    | 'app'
    | 'session'
    | 'create'
    | 'terminal'
    | 'web'
    | 'discover'
  /** The app this row opens, makes a record of, or is a record of, so the row
   *  draws that app's tile (RFC 0332). */
  appId?: string
}

/**
 * Does this look like somewhere on the web?
 *
 * Deliberately conservative, because the failure modes are asymmetric. Treating
 * a search for "readme.md" as a domain sends you to a browser instead of your
 * file, which is jarring; missing a domain just filters to nothing and you
 * press ⌘K again. So: an explicit scheme always wins, and a bare word only
 * counts if it has a dot, no spaces, and a plausible TLD.
 */
export function looksLikeUrl(raw: string): boolean {
  const q = raw.trim()
  if (!q || /\s/.test(q)) return false
  if (/^https?:\/\//i.test(q)) return true
  // A scheme we will NOT open is still a url, and must not fall through to
  // being treated as a search: file:// and friends are refused, not searched.
  if (/^[a-z][a-z0-9+.-]*:/i.test(q)) return false
  // LABELS, plural. This was `^[^./]+\.[a-z]{2,}(...)`, which allows exactly
  // one label before the suffix — so `anthropic.com` was a url and
  // `www.anthropic.com` was not, along with every other subdomain anybody
  // types: docs.*, mail.*, api.*. The row simply did not appear, which reads as
  // the autocomplete being unreliable rather than as a rule.
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}(\/|$|\?|#)/i.test(q)) return false
  // "readme.md" satisfies every structural rule for a domain: one dot, and a
  // two-letter suffix that is indistinguishable from a TLD. Structure alone
  // cannot separate a filename from a domain, so the last discriminator is the
  // suffix itself. A path segment rescues the genuine case (`example.md/docs`),
  // which is why this only rejects a bare `name.ext`.
  const bare = q.split(/[/?#]/)[0] ?? ''
  const ext = bare.slice(bare.lastIndexOf('.') + 1).toLowerCase()
  if (bare === q && FILE_EXTENSIONS.has(ext)) return false
  return true
}

/**
 * Suffixes that are far more likely to be a file in this project than a
 * domain. Not exhaustive and does not need to be: a miss shows no results and
 * costs one retry, while a false positive throws you into a browser.
 */
const FILE_EXTENSIONS = new Set([
  'md',
  'markdown',
  'txt',
  'json',
  'yml',
  'yaml',
  'toml',
  'lock',
  'ts',
  'tsx',
  'js',
  'jsx',
  'mjs',
  'cjs',
  'html',
  'css',
  'scss',
  'py',
  'rs',
  'go',
  'rb',
  'java',
  'kt',
  'swift',
  'sh',
  'bash',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'svg',
  'webp',
  'pdf',
  'csv',
])

/** The url to actually open, always absolute and always http(s). */
export function toUrl(raw: string): string | null {
  const q = raw.trim()
  if (!looksLikeUrl(q)) return null
  const withScheme = /^https?:\/\//i.test(q) ? q : `https://${q}`
  try {
    const u = new URL(withScheme)
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null
  } catch {
    return null
  }
}

/** Substring match on title and hint, most relevant first (title beats hint). */
export function rankTargets(
  targets: CommandTarget[],
  query: string,
): CommandTarget[] {
  const q = query.trim().toLowerCase()
  if (!q) return targets.slice(0, 12)
  const scored: Array<{ t: CommandTarget; score: number }> = []
  for (const t of targets) {
    // `String()` because a title can come straight off the log, where a
    // stored string is an ImmutableString and not a JS string. Calling a
    // string method on one throws, and a throw here takes the whole Launcher
    // to a blank page rather than dropping one row.
    const title = String(t.title ?? '').toLowerCase()
    const hint = String(t.hint ?? '').toLowerCase()
    // A prefix match is what you meant; a title match beats a path match.
    let score = title.startsWith(q)
      ? 3
      : title.includes(q)
        ? 2
        : hint.includes(q)
          ? 1
          : 0
    // A create action outranks the records it would sit among, so typing an
    // app's name and pressing Enter makes one rather than opening whichever
    // record happened to sort first.
    if (score > 0 && t.kind === 'create') score += 3
    if (score > 0) scored.push({ t, score })
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, 12)
    .map((s) => s.t)
}

/**
 * The title a session gets when it is born from ⌘K.
 *
 * The typed text IS the title, trimmed to one line: a session called "Session
 * 7" tells you nothing in a list, and the first message is the only thing that
 * ever described it anyway. The worker renames it later if it wants to; this
 * just has to beat a counter.
 */
export function sessionTitleFor(text: string): string {
  const line = (text.trim().split('\n')[0] ?? '').trim()
  if (!line) return 'New session'
  return line.length > 60 ? `${line.slice(0, 59).trimEnd()}\u2026` : line
}

/**
 * ⌘K rows for every project but the window's (RFC 0336 S2), from the catalogs
 * the sidebar already holds. Documents and sessions only: an app or a "new …"
 * action belongs to the project you are in. Each row's hint leads with its
 * project, so two same-titled rows can be told apart, and `owner` says where a
 * picked row opens, since the row itself carries only an id.
 */
export function targetsElsewhere(
  projects: ReadonlyArray<{ projectId: string; displayName: string }>,
  docs: ReadonlyMap<string, ProjectDoc>,
  /** The app a document opens with in its own project, so its row draws that
   *  app's tile as the window's own rows do (RFC 0332). */
  appFor?: (doc: ProjectDoc, documentId: string) => string | undefined,
): { rows: CommandTarget[]; owner: Map<string, string> } {
  const owner = new Map<string, string>()
  const rows: CommandTarget[] = []
  for (const p of projects) {
    for (const r of catalogRows({
      projectId: p.projectId,
      name: p.displayName,
      doc: docs.get(p.projectId),
    })) {
      if (r.kind !== 'document' && r.kind !== 'session') continue
      owner.set(r.id, p.projectId)
      const doc = docs.get(p.projectId)
      const appId =
        r.kind === 'document' && doc ? appFor?.(doc, r.id) : undefined
      rows.push({
        id: r.id,
        title: r.title,
        hint: r.hint ? `${p.displayName} · ${r.hint}` : p.displayName,
        kind: r.kind,
        ...(appId ? { appId } : {}),
      })
    }
  }
  return { rows, owner }
}
